import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { combineReducers } from 'redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { recordModalReducer } from '@app/components/RecordModal/model/recordModalSlice';
import { resetStore } from '@app/store/actions/resetStore';
import {
  AUTH,
  COUNTRIES,
  FORMS,
  MENU_SIDEBAR_STATE,
  ROUTES,
  WORK_SCHEDULE_SERVICE,
  RECOR_MODAL,
} from '@app/store/constants/constants';
import {
  authReducer,
  countriesReducer,
  menuCollapsedReducer,
  routesReducer,
  workScheduleServiceReducer,
} from '@app/store/reducers';
import { middlewares, reducers } from '@app/store/rtkQuery';
import { slices } from '@app/store/slices';

import type { AnyAction } from 'redux';

const combined = combineReducers({
  [AUTH]: authReducer,
  [COUNTRIES]: countriesReducer,
  [ROUTES]: routesReducer,
  [WORK_SCHEDULE_SERVICE]: workScheduleServiceReducer,
  [MENU_SIDEBAR_STATE]: menuCollapsedReducer,
  [RECOR_MODAL]: recordModalReducer,
  ...reducers,
  ...slices,
});

export const rootReducer = (
  state: ReturnType<typeof combined> | undefined,
  action: AnyAction
) => {
  if (action.type === resetStore.type) {
    return combined(undefined, action);
  }
  return combined(state, action);
};

const PERSIST_VERSION = 2;

const persistConfig = {
  key: 'root',
  version: PERSIST_VERSION,
  storage,
  whitelist: [
    AUTH,
    FORMS,
    COUNTRIES,
    ROUTES,
    WORK_SCHEDULE_SERVICE,
    // MENU_SIDEBAR_STATE is intentionally omitted: usePageEffects always calls
    // showFullSidebar() on mount, which dispatches FULL (desktop) or NONE
    // (mobile) regardless of what was stored. Persisting it caused a
    // SMALL → FULL flip during rehydration that produced CLS ~0.0083 on every
    // page load (shifting calendar-container and ant-menu nodes).
    'newAccessRights',
  ],
  blacklist: [],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: { warnAfter: 64 },
    }).concat(...middlewares),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
